import React, { useState } from 'react';
import { Plus, Minus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { debtorService } from '@/lib/api';
import ActaFilesButtons, { companyActaFileUrl } from '@/components/debtor-portal/ActaFilesButtons';

const toUpperCase = (value) => value ? value.toUpperCase() : '';

// Normalize date to yyyy-MM-dd for HTML date input
const normalizeDate = (val) => {
  if (!val) return '';
  const s = String(val).replace(' 00:00:00', '').trim();
  // Already yyyy-MM-dd
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  // DD/MM/YYYY
  const parts = s.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{4})$/);
  if (parts) return `${parts[3]}-${parts[2].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
  return '';
};

const emptyActa = { number: '', infraccion: '', fecha: '', hora: '', monto: '', monto_quita: '', discount_expiry: '', monto_expiry: '' };
const emptyDomain = { plate: '', actas: [{ ...emptyActa }] };

const DebtorEditDialog = ({ open, onOpenChange, debtor, portfolios, customDebtorFields, onSuccess }) => {
  const [editingDebtor, setEditingDebtor] = useState(null);
  const [editInvoiceFile, setEditInvoiceFile] = useState(null);

  React.useEffect(() => {
    if (debtor && open) {
      // Normalize domains/actas for editing
      const domains = debtor.domains?.length > 0
        ? debtor.domains.map(d => ({
            plate: d.plate || '',
            actas: d.actas?.length > 0
              ? d.actas.map(a => ({
                  ...a,
                  number: a.number || '',
                  infraccion: a.infraccion || '',
                  fecha: normalizeDate(a.fecha),
                  hora: a.hora || '',
                  monto: a.monto ?? '',
                  monto_quita: a.monto_quita ?? '',
                  discount_expiry: normalizeDate(a.discount_expiry),
                  monto_expiry: normalizeDate(a.monto_expiry),
                  status: a.status || '',
                }))
              : [{ ...emptyActa }]
          }))
        : debtor.plate
          ? [{ plate: debtor.plate, actas: [{ ...emptyActa }] }]
          : [{ ...emptyDomain }];
      setEditingDebtor({ ...debtor, domains });
      setEditInvoiceFile(null);
    }
  }, [debtor, open]);

  // Domain handlers
  const addDomain = () => setEditingDebtor(prev => ({ ...prev, domains: [...(prev.domains || []), { ...emptyDomain, actas: [{ ...emptyActa }] }] }));
  const updateDomainPlate = (di, value) => {
    const cleaned = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 7);
    setEditingDebtor(prev => {
      const domains = [...prev.domains];
      domains[di] = { ...domains[di], plate: cleaned };
      return { ...prev, domains };
    });
  };
  const removeDomain = (di) => {
    setEditingDebtor(prev => {
      const domains = prev.domains.filter((_, i) => i !== di);
      return { ...prev, domains: domains.length > 0 ? domains : [{ ...emptyDomain, actas: [{ ...emptyActa }] }] };
    });
  };

  // Acta handlers
  const addActa = (di) => {
    setEditingDebtor(prev => {
      const domains = [...prev.domains];
      domains[di] = { ...domains[di], actas: [...(domains[di].actas || []), { ...emptyActa }] };
      return { ...prev, domains };
    });
  };
  const updateActa = (di, ai, field, value) => {
    setEditingDebtor(prev => {
      const domains = [...prev.domains];
      const actas = [...domains[di].actas];
      actas[ai] = { ...actas[ai], [field]: field === 'number' ? toUpperCase(value) : value };
      domains[di] = { ...domains[di], actas };
      return { ...prev, domains };
    });
  };
  const removeActa = (di, ai) => {
    setEditingDebtor(prev => {
      const domains = [...prev.domains];
      const actas = domains[di].actas.filter((_, i) => i !== ai);
      domains[di] = { ...domains[di], actas: actas.length > 0 ? actas : [{ ...emptyActa }] };
      return { ...prev, domains };
    });
  };

  const handleEdit = async () => {
    if (!editingDebtor) return;

    // Validate acta quita rules
    for (const domain of (editingDebtor.domains || [])) {
      for (const acta of (domain.actas || [])) {
        const hasQuita = acta.monto_quita !== '' && acta.monto_quita != null && parseFloat(acta.monto_quita) > 0;
        if (hasQuita) {
          const hasMonto = acta.monto !== '' && acta.monto != null && parseFloat(acta.monto) > 0;
          if (!hasMonto) {
            toast.error(`Acta ${acta.number || '?'}: Si ingresa Monto c/Quita, debe ingresar el Monto original`);
            return;
          }
          if (!acta.discount_expiry) {
            toast.error(`Acta ${acta.number || '?'}: Si ingresa Monto c/Quita, debe ingresar la Fecha Vto. Quita`);
            return;
          }
        }
      }
    }

    try {
      const customFields = {};
      Object.keys(editingDebtor).forEach(key => {
        if (key.startsWith('custom_') && editingDebtor[key]) customFields[key.replace('custom_', '')] = editingDebtor[key];
      });

      // Build domains payload
      const domains = editingDebtor.domains?.filter(d => d.plate?.trim()).map(d => ({
        plate: d.plate,
        actas: d.actas?.filter(a => a.number?.trim()).map(a => ({
          ...a,
          number: a.number,
          infraccion: a.infraccion || '',
          fecha: a.fecha || null,
          hora: a.hora || null,
          monto: a.monto !== '' && a.monto != null ? parseFloat(a.monto) : null,
          monto_quita: a.monto_quita !== '' && a.monto_quita != null ? parseFloat(a.monto_quita) : null,
          discount_expiry: a.discount_expiry || null,
          monto_expiry: a.monto_expiry || null,
          status: a.status || null,
        })) || []
      })) || [];

      const payload = {
        dni: editingDebtor.dni || null,
        cuit: editingDebtor.cuit || null,
        client_code: editingDebtor.client_code || null,
        plate: domains[0]?.plate || editingDebtor.plate || null,
        name: editingDebtor.name || null,
        email: editingDebtor.email || null,
        phone: editingDebtor.phone || null,
        total_debt: editingDebtor.total_debt ? parseFloat(editingDebtor.total_debt) : null,
        discount_amount: editingDebtor.discount_amount ? parseFloat(editingDebtor.discount_amount) : null,
        portfolio_id: editingDebtor.portfolio_id || null,
        custom_fields: Object.keys(customFields).length > 0 ? customFields : null,
        domains: domains.length > 0 ? domains : null,
      };

      await debtorService.updateDebtor(editingDebtor.id, payload);

      if (editInvoiceFile && editingDebtor.portfolio_id) {
        try {
          await debtorService.uploadInvoice(editingDebtor.portfolio_id, editingDebtor.id, editInvoiceFile);
          toast.success('Cliente y documento actualizados');
        } catch { toast.warning('Cliente actualizado pero hubo un error al subir el documento'); }
      } else {
        toast.success('Cliente actualizado exitosamente');
      }

      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar cliente');
    }
  };

  if (!editingDebtor) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-debtor-dialog">
        <DialogHeader>
          <DialogTitle>Editar Cliente</DialogTitle>
          <DialogDescription>Modifica los datos del cliente</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div>
            <Label>Nombre Completo *</Label>
            <Input data-testid="edit-name" value={editingDebtor.name || ''} onChange={(e) => setEditingDebtor({ ...editingDebtor, name: toUpperCase(e.target.value) })} placeholder="JUAN PEREZ" className="uppercase" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div><Label>DNI</Label><Input data-testid="edit-dni" value={editingDebtor.dni || ''} onChange={(e) => setEditingDebtor({ ...editingDebtor, dni: toUpperCase(e.target.value) })} placeholder="12345678" className="uppercase" /></div>
            <div><Label>CUIT</Label><Input data-testid="edit-cuit" value={editingDebtor.cuit || ''} onChange={(e) => setEditingDebtor({ ...editingDebtor, cuit: toUpperCase(e.target.value) })} placeholder="20-12345678-9" className="uppercase" /></div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div><Label>Codigo Cliente</Label><Input data-testid="edit-client-code" value={editingDebtor.client_code || ''} onChange={(e) => setEditingDebtor({ ...editingDebtor, client_code: toUpperCase(e.target.value) })} className="uppercase" /></div>
            <div>
              <Label>Cartera</Label>
              <Select value={editingDebtor.portfolio_id || 'none'} onValueChange={(v) => setEditingDebtor({ ...editingDebtor, portfolio_id: v === 'none' ? null : v })}>
                <SelectTrigger data-testid="edit-portfolio"><SelectValue placeholder="Seleccionar cartera" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin cartera</SelectItem>
                  {portfolios.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div><Label>Email</Label><Input data-testid="edit-email" type="email" value={editingDebtor.email || ''} onChange={(e) => setEditingDebtor({ ...editingDebtor, email: e.target.value.toLowerCase() })} placeholder="juan@email.com" /></div>
            <div><Label>Telefono</Label><Input data-testid="edit-phone" value={editingDebtor.phone || ''} onChange={(e) => setEditingDebtor({ ...editingDebtor, phone: e.target.value })} placeholder="+54 11 1234-5678" /></div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div><Label>Monto Deuda</Label><Input data-testid="edit-debt" type="number" value={editingDebtor.total_debt || ''} onChange={(e) => setEditingDebtor({ ...editingDebtor, total_debt: e.target.value })} placeholder="10000" /></div>
            <div><Label>Monto c/Quita</Label><Input data-testid="edit-discount" type="number" value={editingDebtor.discount_amount || ''} onChange={(e) => setEditingDebtor({ ...editingDebtor, discount_amount: e.target.value })} placeholder="8000" /></div>
          </div>

          {/* Domains & Actas Section */}
          <div className="border rounded-lg p-3 bg-slate-50">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm font-medium">Dominios e Infracciones</Label>
              <Button type="button" variant="outline" size="sm" onClick={addDomain} className="h-7 px-2" data-testid="edit-add-domain"><Plus className="w-4 h-4" /></Button>
            </div>
            <div className="space-y-3">
              {(editingDebtor.domains || [emptyDomain]).map((domain, di) => (
                <div key={di} className="border rounded p-2 bg-white">
                  <div className="flex gap-2 mb-2">
                    <Input
                      value={domain.plate}
                      onChange={(e) => updateDomainPlate(di, e.target.value)}
                      placeholder="AD558AD"
                      className="uppercase flex-1"
                      maxLength={7}
                      data-testid={`edit-domain-plate-${di}`}
                    />
                    {(editingDebtor.domains?.length || 1) > 1 && (
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeDomain(di)} className="h-9 px-2 text-red-600 hover:text-red-700"><Trash2 className="w-4 h-4" /></Button>
                    )}
                  </div>
                  <div className="pl-3 border-l-2 border-slate-200">
                    <div className="flex items-center justify-between mb-1">
                      <Label className="text-xs text-slate-600">Actas</Label>
                      <Button type="button" variant="ghost" size="sm" onClick={() => addActa(di)} className="h-6 px-1 text-xs" data-testid={`edit-add-acta-${di}`}><Plus className="w-3 h-3 mr-1" /> Acta</Button>
                    </div>
                    <div className="space-y-2">
                      {(domain.actas || [emptyActa]).map((acta, ai) => (
                        <div key={ai} className={`space-y-1 rounded p-2 ${acta.status === 'paid' ? 'bg-emerald-50 opacity-70' : 'bg-slate-50'}`}>
                          <div className="flex gap-1">
                            <Input value={acta.number} onChange={(e) => updateActa(di, ai, 'number', e.target.value)} placeholder="N° Acta" className="uppercase h-8 text-sm flex-1" data-testid={`edit-acta-number-${di}-${ai}`} />
                            {acta.status === 'paid' && <span className="text-xs text-emerald-700 font-semibold self-center px-1">PAGADA</span>}
                            <ActaFilesButtons acta={acta} fileUrl={companyActaFileUrl} compact />
                            {(domain.actas?.length || 1) > 1 && (
                              <Button type="button" variant="ghost" size="sm" onClick={() => removeActa(di, ai)} className="h-8 px-1 text-red-600 hover:text-red-700"><Minus className="w-3 h-3" /></Button>
                            )}
                          </div>
                          <Input value={acta.infraccion} onChange={(e) => updateActa(di, ai, 'infraccion', e.target.value)} placeholder="Infraccion" className="h-8 text-sm" data-testid={`edit-acta-infraccion-${di}-${ai}`} />
                          <div className="flex gap-1">
                            <Input type="date" value={acta.fecha || ''} onChange={(e) => updateActa(di, ai, 'fecha', e.target.value)} className="h-8 text-sm flex-1" data-testid={`edit-acta-fecha-${di}-${ai}`} />
                            <Input type="time" value={acta.hora || ''} onChange={(e) => updateActa(di, ai, 'hora', e.target.value)} className="h-8 text-sm w-28" data-testid={`edit-acta-hora-${di}-${ai}`} />
                          </div>
                          <div className="flex gap-1">
                            <Input type="number" value={acta.monto ?? ''} onChange={(e) => updateActa(di, ai, 'monto', e.target.value)} placeholder={acta.monto_quita ? 'Monto $ (requerido)' : 'Monto $'} className={`h-8 text-sm flex-1 ${acta.monto_quita && !acta.monto ? 'border-red-400 ring-1 ring-red-300' : ''}`} data-testid={`edit-acta-monto-${di}-${ai}`} />
                            <Input type="number" value={acta.monto_quita ?? ''} onChange={(e) => updateActa(di, ai, 'monto_quita', e.target.value)} placeholder="C/Quita $" className="h-8 text-sm flex-1" data-testid={`edit-acta-quita-${di}-${ai}`} />
                          </div>
                          <div className="flex gap-1">
                            <div className="flex-1">
                              <Label className="text-xs text-slate-500">Vto. Monto</Label>
                              <Input type="date" value={acta.monto_expiry || ''} onChange={(e) => updateActa(di, ai, 'monto_expiry', e.target.value)} className="h-8 text-sm" data-testid={`edit-acta-vto-monto-${di}-${ai}`} />
                            </div>
                            <div className="flex-1">
                              <Label className={`text-xs ${acta.monto_quita && !acta.discount_expiry ? 'text-red-500 font-medium' : 'text-slate-500'}`}>Vto. Quita {acta.monto_quita ? '*' : ''}</Label>
                              <Input type="date" value={acta.discount_expiry || ''} onChange={(e) => updateActa(di, ai, 'discount_expiry', e.target.value)} className={`h-8 text-sm ${acta.monto_quita && !acta.discount_expiry ? 'border-red-400 ring-1 ring-red-300' : ''}`} data-testid={`edit-acta-vto-quita-${di}-${ai}`} />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Upload Document */}
          <div className="border-t pt-4 mt-4">
            <Label className="text-sm font-medium text-slate-700 mb-2 block">Subir Factura / Acta / Documento</Label>
            <Input type="file" accept=".pdf,.png,.jpg,.jpeg,.doc,.docx" onChange={(e) => setEditInvoiceFile(e.target.files?.[0] || null)} className="cursor-pointer" />
            {editInvoiceFile && (
              <div className="mt-2 flex items-center justify-between bg-slate-50 p-2 rounded">
                <span className="text-sm text-slate-700 truncate">{editInvoiceFile.name}</span>
                <Button variant="ghost" size="sm" onClick={() => setEditInvoiceFile(null)} className="text-red-600 hover:text-red-700">Quitar</Button>
              </div>
            )}
            {editingDebtor.portfolio_id && <p className="text-xs text-slate-500 mt-1">El documento se asociara a la cartera seleccionada</p>}
            {!editingDebtor.portfolio_id && editInvoiceFile && <p className="text-xs text-amber-600 mt-1">Selecciona una cartera para poder subir el documento</p>}
          </div>

          {/* Custom Fields */}
          {customDebtorFields.length > 0 && (
            <div className="border-t pt-4 mt-4">
              <Label className="text-sm font-medium text-slate-700 mb-3 block">Campos Personalizados</Label>
              <div className="grid grid-cols-2 gap-3">
                {customDebtorFields.map(field => (
                  <div key={field.field_id}>
                    <Label className="text-sm">{field.field_name}{field.is_required && <span className="text-red-500 ml-1">*</span>}</Label>
                    {field.field_type === 'select' && field.options?.length > 0 ? (
                      <Select value={editingDebtor[`custom_${field.field_id}`] || editingDebtor.custom_fields?.[field.field_id] || ''} onValueChange={(v) => setEditingDebtor({ ...editingDebtor, [`custom_${field.field_id}`]: v })}>
                        <SelectTrigger><SelectValue placeholder={`Seleccionar ${field.field_name}`} /></SelectTrigger>
                        <SelectContent>{field.options.map(opt => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}</SelectContent>
                      </Select>
                    ) : (
                      <Input type={field.field_type === 'number' ? 'number' : field.field_type === 'date' ? 'date' : 'text'} value={editingDebtor[`custom_${field.field_id}`] || editingDebtor.custom_fields?.[field.field_id] || ''} onChange={(e) => setEditingDebtor({ ...editingDebtor, [`custom_${field.field_id}`]: e.target.value })} placeholder={field.field_name} />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-2 pt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1">Cancelar</Button>
            <Button data-testid="edit-save-btn" onClick={handleEdit} className="flex-1 bg-teal-600 hover:bg-teal-700">Guardar Cambios</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DebtorEditDialog;
